import { legalServiceSchema } from "@/lib/firm";
import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { FEES, FIRM, FIRM_ADDRESS, PENINSULA_ADDRESS, faqSchema } from "@/lib/firm";
import { SELL_LOCATIONS } from "@/lib/sell-locations";
import { SELLER_DISCLOSURE_START_LABEL } from "@/lib/seller-disclosure";

const SITE = "https://coastsidelaw.com.au";
const URL = `${SITE}/selling`;

const TITLE = `Selling a Property in Queensland — Fixed Fee ${FEES.sale} | ${FIRM.name}`;
const DESCRIPTION = `Solicitor-led fixed-fee sale conveyancing across the Sunshine Coast and Moreton Bay: ${FEES.sale} incl. GST, seller disclosure statement prepared, free contract review before you sign. Call ${FIRM.phone}.`;

const FAQS = [
  {
    q: "When should I contact a solicitor about selling?",
    a: `Before the agent lists the property. Since ${SELLER_DISCLOSURE_START_LABEL}, sales covered by the seller disclosure scheme require the statement and prescribed certificates before the buyer signs. Certificates take time to obtain, so instructing early helps avoid delays when an offer arrives.`,
  },
  {
    q: `What is included in the ${FEES.sale} fee?`,
    a: "Reviewing the agent's appointment and the contract, preparing the seller disclosure statement, running the contract to settlement, obtaining the bank payout figure, adjusting rates and water, and attending electronic settlement. Searches, certificates and registry fees are charged at cost.",
  },
  {
    q: "What happens if the contract is terminated?",
    a: "If the buyer terminates during the cooling-off period or on finance or building and pest, there is no professional fee for that contract. We carry it into the next one. You only pay for third-party costs already incurred.",
  },
  {
    q: "Can you handle my sale and purchase together?",
    a: "Yes, and it is the better way to do it. We line the settlement dates up where the contracts allow and tell you plainly what bridging exposure you would carry if they do not align.",
  },
  {
    q: "What if I owe more than the property will sell for?",
    a: "Raise it before signing. The lender's payout figure controls whether settlement can occur, and where there is a shortfall the bank has to agree to release the title. There is a path through it, but only if it is arranged early.",
  },
  {
    q: "Do I have to attend an office?",
    a: `No. Signing is electronic and settlement runs through PEXA. Our offices at ${FIRM_ADDRESS} and ${PENINSULA_ADDRESS} are open by appointment if you would prefer to meet.`,
  },
];

export const Route = createFileRoute("/selling/")({
  head: () => ({
    meta: [
      { title: fitTitle(TITLE) },
      { name: "description", content: fitDescription(DESCRIPTION) },
      { property: "og:title", content: fitTitle(TITLE) },
      { property: "og:description", content: fitDescription(DESCRIPTION) },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LegalService",
          name: `${FIRM.name} — sale conveyancing`,
          description: DESCRIPTION,
          telephone: FIRM.phone,
          address: legalServiceSchema.address,
          parentOrganization: { "@id": "https://coastsidelaw.com.au/#legalservice" },
          areaServed: SELL_LOCATIONS.map((l) => ({
            "@type": "Place",
            name: `${l.name}, Queensland`,
          })),
          serviceType: [
            "Sale conveyancing",
            "Seller disclosure statement",
            "Contract review",
            "Simultaneous sale and purchase",
          ],
          offers: [
            {
              "@type": "Offer",
              name: "Fixed-fee sale conveyancing",
              price: FEES.sale.replace("$", "").replace(",", ""),
              priceCurrency: "AUD",
            },
          ],
        }),
      },
    ],
  }),
  component: SellHub,
});

const STEPS = [
  {
    t: "1. Before you list",
    d: "We check the agent's appointment and commission, order the title and certificate material, and prepare the seller disclosure statement so it is ready the moment a buyer wants to sign.",
  },
  {
    t: "2. Contract signing",
    d: "We read the contract before you sign it — deposit, dates, inclusions and any condition that lets the buyer out late. Free, and with no obligation to use us for the sale.",
  },
  {
    t: "3. The conditional period",
    d: "Cooling-off, finance and building and pest dates diarised and enforced. If the buyer wants an extension or a price reduction, you get advice on it, not just a forwarded email.",
  },
  {
    t: "4. Settlement",
    d: "Bank payout obtained, rates and water adjusted, net proceeds confirmed in writing, and electronic settlement booked and attended. Keys released when the money lands.",
  },
];

function SellHub() {
  return (
    <>
      <PageHero
        eyebrow="Selling"
        title="Selling a property in Queensland"
        lead={`Fixed fee ${FEES.sale} including GST, seller disclosure statement prepared before you list, and no professional fee if the contract is terminated.`}
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="rounded-sm border border-border bg-card p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Sale conveyancing
            </p>
            <p className="mt-2 font-serif text-3xl text-foreground">{FEES.sale}</p>
            <p className="mt-1 text-sm text-muted-foreground">{FEES.saleNote}</p>
          </div>
          <div className="rounded-sm border border-border bg-card p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Contract review before signing
            </p>
            <p className="mt-2 font-serif text-3xl text-foreground">{FEES.contractReview}</p>
            <p className="mt-1 text-sm text-muted-foreground">{FEES.contractReviewNote}</p>
          </div>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Searches, body corporate certificates, council and water information and registry fees are
          set by others and charged at cost. Everything is quoted in writing before you engage us.
        </p>

        <h2 className="mt-14 font-serif text-3xl text-foreground">How a sale runs</h2>
        <div className="mt-6 grid gap-5 sm:grid-cols-2">
          {STEPS.map((s) => (
            <div key={s.t} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-lg text-foreground">{s.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
            </div>
          ))}
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">
          Get the disclosure statement right
        </h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          The seller disclosure regime that started on {SELLER_DISCLOSURE_START_LABEL} shifted real risk onto sellers.
          If the statement is incomplete, or handed over after the buyer signs, the buyer can
          terminate at any time before settlement — including after your own purchase is locked in.
          We prepare it as part of the fixed fee. See the{" "}
          <Link to="/seller-disclosure" className="font-semibold text-primary underline">
            seller disclosure guide
          </Link>{" "}
          for what has to be attached.
        </p>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Selling by area</h2>
        <ul className="mt-5 flex flex-wrap gap-2">
          {SELL_LOCATIONS.map((l) => (
            <li key={l.slug}>
              <Link
                to="/selling/$suburb"
                params={{ suburb: l.slug }}
                className="inline-block rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary"
              >
                Selling in {l.name}
              </Link>
            </li>
          ))}
        </ul>

        <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} heading="Who will act for you" />

        <h2 className="mt-14 font-serif text-3xl text-foreground">Common questions</h2>
        <div className="mt-6">
          <FaqList items={FAQS} />
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Related pages</h2>
        <ul className="mt-5 space-y-2 text-sm">
          <li>
            <Link to="/conveyancing" className="font-semibold text-primary underline">
              Fixed-fee conveyancing — sale and purchase
            </Link>
          </li>
          <li>
            <Link to="/seller-disclosure" className="font-semibold text-primary underline">
              Seller disclosure statements in Queensland
            </Link>
          </li>
          <li>
            <Link to="/selling-a-business-queensland" className="font-semibold text-primary underline">
              Selling a business in Queensland
            </Link>
          </li>
        </ul>

        <div className="mt-14">
          <EnquiryForm heading="Get a fixed-fee quote for your sale" />
        </div>
      </section>

      <CtaBand
        title="Thinking of listing? Talk to us first."
        body={`Call ${FIRM.phone} or send the property address through. We will tell you what the disclosure statement needs and confirm the fee in writing.`}
      />
      <div className="h-20" />
    </>
  );
}
